// src/pages/Root.tsx
import { useEffect, useState } from 'react';
import {
  Outlet,
  useLoaderData,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useSetRecoilState } from 'recoil';

import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';

import Header from '@components/common/Header';
import LeftSideBar from '@components/common/LeftSideBar';
import AppPageHeader from '@components/common/AppPageHeader';

import { authState, userState } from '@recoils/atoms';
import { UserGroup, type UserResponse } from '@models/user';

import {
  PageHeaderProvider,
  usePageHeader,
} from '@components/common/pageHeader.context';

import { ConfirmProvider } from '@components/common/confirm/ConfirmProvider';
import { SystemAlertProvider } from '@components/common/systemAlert/SystemAlertProvider';

const DRAWER_WIDTH = 240;

/**
 * RootLayout
 *
 * Responsibilities:
 * - Sync authenticated user state from loader data
 * - Handle initial "/" redirect by user group
 * - Reset AppPageHeader slots on route change
 * - Render global layout (Header / Sidebar / Main)
 */
function RootLayout() {
  const me = useLoaderData() as UserResponse | null;

  const setAuth = useSetRecoilState(authState);
  const setUser = useSetRecoilState(userState);

  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleDrawer = () => setMobileOpen((prev) => !prev);
  const closeDrawer = () => setMobileOpen(false);

  // PageHeader slots (provided by PageHeaderProvider)
  const { slots } = usePageHeader();

  /**
   * Sync auth & user state whenever loader data changes.
   */
  useEffect(() => {
    if (me) {
      setAuth(true);
      setUser(me);
    } else {
      setAuth(false);
      setUser(null);
    }
  }, [me, setAuth, setUser]);

  /**
   * Initial landing redirect.
   * "/" → partner: /orders, admin: /home
   */
  useEffect(() => {
    if (!me) return;
    if (location.pathname !== '/') return;

    navigate(
      me.group === UserGroup.PARTNER ? '/orders' : '/home',
      { replace: true },
    );
  }, [me, location.pathname, navigate]);

  return (
    <Box sx={{ display: 'flex' }}>
      <Header onToggleDrawer={toggleDrawer} />

      <LeftSideBar
        mobileOpen={mobileOpen}
        onClose={closeDrawer}
        drawerWidth={DRAWER_WIDTH}
      />

      <Box
        id="app-main-scroll"
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          pb: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          height: '100vh',
          overflowY: 'auto',
          bgcolor: 'background.default',
        }}
      >
        {/* Spacer for AppBar */}
        <Toolbar />

        {/* Unified page header (breadcrumb / title / actions) */}
        <AppPageHeader slots={slots} />

        {/* Route content */}
        <Outlet />
      </Box>
    </Box>
  );
}

/**
 * Root
 *
 * Wraps RootLayout with PageHeaderProvider.
 * This ensures page header slots are globally available.
 */
export default function Root() {
  return (
    <ConfirmProvider>
      <SystemAlertProvider>
        <PageHeaderProvider>
          <RootLayout />
        </PageHeaderProvider>
      </SystemAlertProvider>
    </ConfirmProvider>
  );
}
